import React from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import commaNumber from "comma-number";
import { formatDateOnly } from "../../../../lib/functions";

const format = commaNumber.bindWith(",", ".");
const fmt = (val) => (val === null || val === undefined ? "" : format(parseFloat(val).toFixed(2)));

const STARTING_ROW_ID = "starting-balance";

const OutstandingPdcTable = ({ printMode = false }) => {
  const { banks, rows, grandStartingTotal, grandClosingTotal, loading } = useSelector(
    (state) => state.outstandingPdc
  );

  const startingRow = {
    id: STARTING_ROW_ID,
    isStartingRow: true,
    party_name: "Starting Balance",
    bank_balances: banks.reduce(
      (acc, b) => ({ ...acc, [b.acc_id]: b.starting_balance }),
      {}
    ),
    combined_total: grandStartingTotal,
  };

  const dataSource = [startingRow, ...rows];

  const hasUnassigned = rows.some((r) => !r.expected_bank_acc_id);

  if (printMode) {
    return (
      <table className="ledger-print-table" style={{ tableLayout: "fixed", width: "100%" }}>
        <thead>
          <tr className="ledger-print-heading">
            <td>Date</td>
            <td>Party</td>
            <td>Voucher No</td>
            <td>Cheque Number</td>
            {banks.map((b) => (
              <td key={b.acc_id} colSpan={3} style={{ textAlign: "center" }}>
                {b.acc_name}
              </td>
            ))}
            {hasUnassigned && (
              <td colSpan={2} style={{ textAlign: "center" }}>
                Unassigned
              </td>
            )}
            <td>Combined Total</td>
          </tr>
          <tr className="ledger-print-heading">
            <td colSpan={4} />
            {banks.map((b) => (
              <React.Fragment key={b.acc_id}>
                <td style={{ textAlign: "right" }}>Out</td>
                <td style={{ textAlign: "right" }}>In</td>
                <td style={{ textAlign: "right" }}>Balance</td>
              </React.Fragment>
            ))}
            {hasUnassigned && (
              <>
                <td style={{ textAlign: "right" }}>Out</td>
                <td style={{ textAlign: "right" }}>In</td>
              </>
            )}
            <td />
          </tr>
        </thead>
        <tbody>
          {dataSource.map((row, index) => (
            <tr key={row.id || index} style={row.isStartingRow ? { fontWeight: "bold" } : undefined}>
              <td>{row.isStartingRow ? "" : formatDateOnly(row.date)}</td>
              <td>{row.party_name}</td>
              <td>{row.isStartingRow ? "" : row.voucher_no}</td>
              <td>{row.isStartingRow ? "" : row.cheque_number}</td>
              {banks.map((b) => (
                <React.Fragment key={b.acc_id}>
                  <td style={{ textAlign: "right" }}>
                    {!row.isStartingRow && row.expected_bank_acc_id === b.acc_id && row.direction === "out" ? fmt(row.amount) : ""}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {!row.isStartingRow && row.expected_bank_acc_id === b.acc_id && row.direction === "in" ? fmt(row.amount) : ""}
                  </td>
                  <td style={{ textAlign: "right" }}>{fmt(row.bank_balances?.[b.acc_id])}</td>
                </React.Fragment>
              ))}
              {hasUnassigned && (
                <>
                  <td style={{ textAlign: "right" }}>
                    {!row.isStartingRow && !row.expected_bank_acc_id && row.direction === "out" ? fmt(row.amount) : ""}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {!row.isStartingRow && !row.expected_bank_acc_id && row.direction === "in" ? fmt(row.amount) : ""}
                  </td>
                </>
              )}
              <td style={{ textAlign: "right" }}>{fmt(row.combined_total)}</td>
            </tr>
          ))}
          <tr className="ledger-total-row">
            <td colSpan={4 + banks.length * 3 + (hasUnassigned ? 2 : 0)} style={{ textAlign: "right" }}>
              Closing Combined Total
            </td>
            <td style={{ textAlign: "right" }}>{fmt(grandClosingTotal)}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  const baseColumns = [
    {
      title: "Date",
      dataIndex: "date",
      width: 120,
      render: (v, r) => (r.isStartingRow ? <b>Starting Balance</b> : formatDateOnly(v)),
    },
    { title: "Party", dataIndex: "party_name" },
    { title: "Voucher No", dataIndex: "voucher_no", render: (v, r) => (r.isStartingRow ? "" : v) },
    { title: "Cheque Number", dataIndex: "cheque_number", render: (v, r) => (r.isStartingRow ? "" : v) },
  ];

  const bankColumnGroups = banks.map((bank) => ({
    title: bank.acc_name,
    children: [
      {
        title: "Out",
        align: "right",
        width: 110,
        render: (_, r) =>
          !r.isStartingRow && r.expected_bank_acc_id === bank.acc_id && r.direction === "out"
            ? fmt(r.amount)
            : "",
      },
      {
        title: "In",
        align: "right",
        width: 110,
        render: (_, r) =>
          !r.isStartingRow && r.expected_bank_acc_id === bank.acc_id && r.direction === "in"
            ? fmt(r.amount)
            : "",
      },
      {
        title: "Balance",
        align: "right",
        width: 130,
        render: (_, r) => <b>{fmt(r.bank_balances?.[bank.acc_id])}</b>,
      },
    ],
  }));

  const unassignedColumnGroup = hasUnassigned
    ? [
        {
          title: "Unassigned",
          children: [
            {
              title: "Out",
              align: "right",
              width: 110,
              render: (_, r) =>
                !r.isStartingRow && !r.expected_bank_acc_id && r.direction === "out" ? fmt(r.amount) : "",
            },
            {
              title: "In",
              align: "right",
              width: 110,
              render: (_, r) =>
                !r.isStartingRow && !r.expected_bank_acc_id && r.direction === "in" ? fmt(r.amount) : "",
            },
          ],
        },
      ]
    : [];

  const totalColumn = {
    title: "Combined Total",
    dataIndex: "combined_total",
    align: "right",
    width: 150,
    render: (v) => <b>{fmt(v)}</b>,
  };

  const columns = [...baseColumns, ...bankColumnGroups, ...unassignedColumnGroup, totalColumn];

  return (
    <Table
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      rowClassName={(record) => (record.isStartingRow ? "outstanding-pdc-starting-row" : "")}
      pagination={false}
      bordered
      scroll={{ x: "max-content" }}
      size="middle"
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={4 + banks.length * 3 + (hasUnassigned ? 2 : 0)}>
            <strong>Closing Combined Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="right">
            <strong>{fmt(grandClosingTotal)}</strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default OutstandingPdcTable;
